import React from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import type { ReactNode } from 'react'

export type SortDirection = 'asc' | 'desc' | null

/**
 * Generic sorting function for arrays
 */
export function sortData<T>(
  data: T[],
  sortKey: keyof T,
  direction: SortDirection
): T[] {
  if (!direction || !sortKey) {
    return data
  }

  return [...data].sort((a, b) => {
    const aValue = a[sortKey]
    const bValue = b[sortKey]

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0
    if (aValue == null) return direction === 'asc' ? -1 : 1
    if (bValue == null) return direction === 'asc' ? 1 : -1

    // Handle string comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue, 'pl', { sensitivity: 'base' })
      return direction === 'asc' ? comparison : -comparison
    }

    // Handle number comparison
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue
    }

    // Handle date comparison
    if (aValue instanceof Date && bValue instanceof Date) {
      return direction === 'asc' 
        ? aValue.getTime() - bValue.getTime()
        : bValue.getTime() - aValue.getTime()
    }

    // Handle string dates
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const aDate = new Date(aValue)
      const bDate = new Date(bValue)
      if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
        return direction === 'asc'
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime()
      }
    }

    // Fallback to string comparison
    const aStr = String(aValue)
    const bStr = String(bValue)
    const comparison = aStr.localeCompare(bStr, 'pl', { sensitivity: 'base' })
    return direction === 'asc' ? comparison : -comparison
  })
}

/**
 * Get sort icon based on direction
 */
export function getSortIcon(direction: SortDirection): ReactNode {
  if (direction === 'asc') {
    return React.createElement(ArrowUp, { className: "ml-2 h-4 w-4" })
  }
  if (direction === 'desc') {
    return React.createElement(ArrowDown, { className: "ml-2 h-4 w-4" })
  }
  return React.createElement(ArrowUpDown, { className: "ml-2 h-4 w-4 opacity-50" })
}

/**
 * Toggle sort direction when clicking on a column
 */
export function getNextSortDirection(
  currentField: string | null,
  clickedField: string,
  currentDirection: SortDirection
): SortDirection {
  if (currentField !== clickedField) {
    return 'asc'
  }
  if (currentDirection === 'asc') {
    return 'desc'
  }
  if (currentDirection === 'desc') {
    return null
  }
  return 'asc'
}
